/* Main-thread orchestration of an exercise run (02 §2.8).
 *
 * The whole job of this file is the 10-second wall clock. The worker cannot
 * enforce it on itself — a `while True:` never yields — so the timer lives here
 * and the enforcement is `worker.terminate()`.
 *
 * The clock starts when the worker says `ready`, not when it is created: booting
 * a 13 MB interpreter is the machine's time, not the learner's, and charging
 * boot time against a 10 s budget would fail correct answers on a cold start.
 */

import { CODE_RUN_TIMEOUT_MS, PYODIDE_BASE, SQLITE_BASE } from '@ascent/shared';
import type { RunOutcome, TablePreview, WorkerRequest, WorkerResponse } from './types';
import { pythonRunnable } from './pack';

/** The minimum of the Worker surface this file drives — and all a test needs. */
export interface RunnerWorker {
  onmessage: ((ev: MessageEvent<WorkerResponse>) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  postMessage(data: WorkerRequest): void;
  terminate(): void;
}

export type WorkerFactory = (kind: 'python' | 'sql') => RunnerWorker;

const spawn: WorkerFactory = (kind) => {
  // Same-origin module workers, emitted by Vite as hashed chunks. Not blob: —
  // a same-origin script URL is what lets the service worker intercept the
  // runtime fetches this worker makes, which is what makes offline work.
  return (kind === 'python'
    ? new Worker(new URL('./python.worker.ts', import.meta.url), { type: 'module' })
    : new Worker(new URL('./sql.worker.ts', import.meta.url), { type: 'module' })) as unknown as RunnerWorker;
};

export function runInWorker(
  kind: 'python' | 'sql',
  request: WorkerRequest,
  factory: WorkerFactory = spawn,
): Promise<RunOutcome> {
  return new Promise<RunOutcome>((resolve) => {
    const worker = factory(kind);
    const created = Date.now();
    let timer: ReturnType<typeof setTimeout> | null = null;
    let settled = false;

    const finish = (outcome: RunOutcome): void => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      worker.terminate();
      resolve(outcome);
    };

    worker.onmessage = (ev: MessageEvent<WorkerResponse>) => {
      const msg = ev.data;
      if (msg.type === 'ready') {
        // The learner's 10 seconds start now.
        timer = setTimeout(() => finish({ status: 'timeout', elapsedMs: CODE_RUN_TIMEOUT_MS }), CODE_RUN_TIMEOUT_MS);
        return;
      }
      if (msg.type === 'result') {
        finish(msg.result);
        return;
      }
      // `describe` is answered by describeSql, never here.
      if (msg.type !== 'fatal') return;
      finish({ status: 'error', message: msg.message, stdout: '', elapsedMs: Date.now() - created });
    };

    worker.onerror = () => {
      finish({
        status: 'error',
        message: 'The runtime failed to load. Install the offline pack in Settings, or connect once.',
        stdout: '',
        elapsedMs: Date.now() - created,
      });
    };

    worker.postMessage(request);
  });
}

export async function runPython(code: string, checksPy: string): Promise<RunOutcome> {
  if (!(await pythonRunnable())) return { status: 'blocked', reason: 'pack_missing' };
  return runInWorker('python', { kind: 'python', code, checksPy, base: PYODIDE_BASE });
}

export function runSql(
  sql: string,
  seedSql: string,
  expectedRows: unknown[][],
  orderSensitive: boolean,
): Promise<RunOutcome> {
  return runInWorker('sql', { kind: 'sql', sql, seedSql, expectedRows, orderSensitive, base: SQLITE_BASE });
}

/**
 * Read back the seeded database so the learner can see what they are querying.
 *
 * Runs the seed in the same engine the answer will run in, so the schema shown
 * is the schema that exists — not a description that can drift from it.
 */
export function describeSql(seedSql: string, factory: WorkerFactory = spawn): Promise<TablePreview[]> {
  return new Promise<TablePreview[]>((resolve) => {
    const worker = factory('sql');
    let settled = false;
    const finish = (tables: TablePreview[]): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      worker.terminate();
      resolve(tables);
    };

    // A seed that hangs must not leave the panel spinning forever.
    const timer = setTimeout(() => finish([]), CODE_RUN_TIMEOUT_MS);

    worker.onmessage = (ev: MessageEvent<WorkerResponse>) => {
      const msg = ev.data;
      if (msg.type === 'describe') finish(msg.tables);
      else if (msg.type === 'fatal') finish([]);
    };
    worker.onerror = () => finish([]);
    worker.postMessage({ kind: 'sql_describe', seedSql, base: SQLITE_BASE });
  });
}

/**
 * `reading` exercises have no runtime at all — exact-match on the answer text.
 * Comparison is trimmed and case-insensitive; anything stricter turns a correct
 * answer with a trailing space into a wrong one, which teaches nothing.
 */
export function checkReading(answer: string, accepted: string[]): RunOutcome {
  const norm = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');
  const given = norm(answer);
  const passed = accepted.some((a) => norm(a) === given);
  return {
    status: 'ok',
    passed,
    stdout: '',
    checks: [{ name: 'answer', passed, message: passed ? 'matches' : 'not the expected answer' }],
    elapsedMs: 0,
  };
}
